import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import apiClient from "../../api/client";
import { useSyncCooldown, fmtCountdown } from "../NmsDevices/useSyncCooldown";
import "./OnuDetailsModal.css";

// Same endpoint the parent already calls to load this modal.
// TODO: set this to your onu_details.php path (relative to apiClient's baseURL).
const ONU_DETAILS_URL = "/olt/onu_details.php";

// Shows the OLT/ONU monitoring service's lookup result for a customer's
// MAC address — onu_details.php looks the MAC up from the broadband
// provider first, then queries the shared OLT/ONU service with it.
//
// Props:
//   open     — whether the modal is visible
//   onClose  — () => void
//   loading  — true while the first onu_details.php load is in flight
//   error    — error message string, or null
//   data     — the onu_details.php success payload:
//              { mac, found, best, seen_on, synced, sync_throttled, retry_after }
//   internetId — optional. The modal syncs by itself using this (or the
//              internet_id that onu_details.php now returns), so no parent
//              changes are needed for the Sync button.
//   onSync   — optional () => Promise<data> to do the sync in the parent
//              instead. Must resolve with the new payload.
const OnuDetailsModal = ({ open, onClose, loading, error, data, internetId, onSync, inline = false }) => {
  const [syncing, setSyncing] = useState(false);
  // Result of our own sync — shown instead of `data` until the parent reloads.
  const [syncedData, setSyncedData] = useState(null);

  useEffect(() => {
    setSyncedData(null);
  }, [data, open]);

  const view = syncedData || data;
  const syncId = internetId ?? view?.internet_id ?? null;
  // The ONU "Sync" button was removed (devices are synced from the dashboard's Devices
  // button instead), so this is always off.
  const canSync = false;

  const cooldownKey = syncId ? `onu:${syncId}` : view?.mac ? `onu:${view.mac}` : null;
  const [cooldown, startCooldown] = useSyncCooldown(cooldownKey);

  // Close on Escape + lock background scroll while the modal is open.
  useEffect(() => {
    // Shown inside the account's tab panel: no Escape-to-close, no scroll lock.
    if (!open || inline) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, inline]);

  if (!open) return null;

  const best = view?.best || null;
  const seenOn = Array.isArray(view?.seen_on) ? view.seen_on : [];

  const runSync = async () => {
    if (onSync) return onSync();
    const { data: res } = await apiClient.get(ONU_DETAILS_URL, {
      params: { internet_id: syncId, sync: 1 },
      timeout: 150000, // a live OLT read can take a while
    });
    if (!res?.success) throw new Error(res?.message || "ONU sync failed");
    setSyncedData(res);
    return res;
  };

  const handleSync = async () => {
    if (!canSync || syncing || cooldown > 0) return;
    setSyncing(true);
    try {
      const res = await runSync();

      if (res?.sync_throttled) {
        const wait = Number(res.retry_after) || 60;
        toast.info("This connection was synced moments ago — showing the latest reading.");
        startCooldown(wait * 1000);
        return;
      }

      if (res && res.found === false) {
        toast.warn("Sync finished, but this MAC still isn't seen on any OLT.");
      } else {
        toast.success("ONU details synced from the OLT.");
      }
      startCooldown();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "ONU sync failed");
      startCooldown();
    } finally {
      setSyncing(false);
    }
  };

  const fmtDate = (value) => {
    if (!value) return "--";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const fmtPower = (value) => {
    if (value === null || value === undefined) return "--";
    return `${value} dBm`;
  };

  // Rough, generally-accepted GPON/EPON optical power bands — not
  // provider-specific, just enough to flag an obviously bad reading at
  // a glance. Anything missing just renders neutral (no badge color).
  const powerLevel = (value) => {
    if (value === null || value === undefined) return "";
    if (value <= -27) return "power-critical";
    if (value <= -24) return "power-warning";
    return "power-good";
  };

  // data_age_seconds replaces the old boolean "stale" flag from the
  // /by-mac/ endpoint — turn it into both a human-readable age and a
  // simple "this reading is old" warning past an hour.
  const fmtAge = (seconds) => {
    if (seconds === null || seconds === undefined) return null;
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    return remMins ? `${hrs}h ${remMins}m` : `${hrs}h`;
  };

  const statusTone = (status) => {
    const value = String(status || "").toLowerCase();
    if (value.includes("online")) return "online";
    if (
      value.includes("offline") ||
      value.includes("los") ||
      value.includes("down")
    ) {
      return "offline";
    }
    return "unknown";
  };

  const rxLevelTone = (level) => {
    if (!level) return "";
    return String(level).toLowerCase() === "ok" ? "power-good" : "power-warning";
  };

  const isStale =
    typeof best?.data_age_seconds === "number" && best.data_age_seconds > 3600;
  const status = statusTone(best?.onu_status);
  const showEmpty = !loading && !error && (!view?.found || !best);

  return (
    <div className={`onu-modal-overlay${inline ? " onu-modal-overlay--inline" : ""}`} onClick={inline ? undefined : onClose}>
      <div
        className="onu-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onu-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="onu-modal-header">
          <div className="onu-modal-heading">
            <h3 id="onu-modal-title">
              <span className="onu-modal-icon" aria-hidden="true">
                📡
              </span>
              ONU Details
            </h3>
          </div>

          <div className="onu-modal-header-side">
            {best && (
              <span className={`onu-badge onu-badge--${status}`}>
                <span className="onu-badge-dot" aria-hidden="true" />
                {best.onu_status || "unknown"}
              </span>
            )}
            {isStale && (
              <span className="onu-badge onu-badge--stale">
                Reading {fmtAge(best.data_age_seconds)} old
              </span>
            )}
            {canSync && !loading && (
              syncing ? (
                <span className="onu-sync-btn is-busy">
                  <span className="onu-sync-spinner" aria-hidden="true" />
                  Syncing…
                </span>
              ) : cooldown > 0 ? (
                <span
                  className="onu-sync-wait"
                  title="Sync is paused for 1 minute after each sync"
                >
                  Sync in {fmtCountdown(cooldown)}
                </span>
              ) : (
                <button
                  type="button"
                  className="onu-sync-btn"
                  onClick={handleSync}
                  title="Read this ONU live from the OLT"
                >
                  ⟳ Sync
                </button>
              )
            )}
            {!inline && <button
              type="button"
              className="onu-modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>}
          </div>
        </header>

        <div className="onu-modal-body">
          {syncing && (
            <p className="onu-sync-note">
              Reading live from the OLT — this can take up to a minute…
            </p>
          )}

          {loading && (
            <div className="onu-modal-state">
              <span className="onu-modal-spinner" aria-hidden="true" />
              <p>Looking up ONU details…</p>
            </div>
          )}

          {!loading && error && (
            <div className="onu-modal-state onu-modal-state--error">
              <div className="onu-modal-state-icon" aria-hidden="true">
                ⚠️
              </div>
              <p>{error}</p>
            </div>
          )}

          {showEmpty && (
            <div className="onu-modal-state">
              <div className="onu-modal-state-icon" aria-hidden="true">
                🔍
              </div>
              <p>No ONU record found for this connection's MAC address.</p>
              {view?.mac && (
                <p className="onu-modal-mac-tried">Looked up: {view.mac}</p>
              )}
            </div>
          )}

          {!loading && !error && best && (
            <div className={syncing ? "onu-is-syncing" : undefined}>
              <div className="onu-modal-grid">
                <section className="onu-modal-card">
                  <h4>OLT / Port</h4>
                  <div className="onu-modal-row">
                    <span>OLT</span>
                    <strong>{best.device_name || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>OLT IP</span>
                    <strong className="onu-mono">{best.device_ip || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Location</span>
                    <strong>{best.device_location || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>PON Port</span>
                    <strong className="onu-mono">
                      {best.port_name || best.pon || "--"}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>ONU ID</span>
                    <strong>{best.onu_id ?? "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>ONU Type</span>
                    <strong>{best.onu_type || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>VLAN</span>
                    <strong>{best.vlan ?? "--"}</strong>
                  </div>
                </section>

                <section className="onu-modal-card">
                  <h4>Signal</h4>
                  <div className="onu-modal-row">
                    <span>ONU Rx Power</span>
                    <strong className={powerLevel(best.onu_rx_power)}>
                      {fmtPower(best.onu_rx_power)}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>ONU Tx Power</span>
                    <strong className="onu-mono">
                      {fmtPower(best.onu_tx_power)}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Rx Level</span>
                    <strong className={rxLevelTone(best.rx_level)}>
                      {best.rx_level || "--"}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Temperature</span>
                    <strong>
                      {best.temperature_c !== null &&
                      best.temperature_c !== undefined
                        ? `${best.temperature_c} °C`
                        : "--"}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Voltage</span>
                    <strong>
                      {best.voltage_v !== null && best.voltage_v !== undefined
                        ? `${best.voltage_v} V`
                        : "--"}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Bias Current</span>
                    <strong>
                      {best.bias_ma !== null && best.bias_ma !== undefined
                        ? `${best.bias_ma} mA`
                        : "--"}
                    </strong>
                  </div>
                </section>

                <section className="onu-modal-card">
                  <h4>MAC / Operator</h4>
                  <div className="onu-modal-row">
                    {best.onu_mac === null || best.onu_mac === undefined ? (
                      <>
                        <span>ONU Serial</span>
                        <strong className="onu-mono">{best.onu_serial || "--"}</strong>
                      </>
                    ) : (
                      <>
                        <span>ONU MAC</span>
                        <strong className="onu-mono">{best.onu_mac || "--"}</strong>
                      </>
                    )}
                  </div>
                  <div className="onu-modal-row">
                    <span>Customer MAC</span>
                    <strong className="onu-mono">
                      {best.customer_mac || view.mac || "--"}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Operator</span>
                    <strong>
                      {best.operator_name || best.operator_phone || "--"}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Last Seen</span>
                    <strong>{fmtDate(best.last_seen)}</strong>
                  </div>
                </section>
              </div>

              {seenOn.length > 1 && (
                <section className="onu-modal-matches">
                  <h4>Seen on ({seenOn.length})</h4>
                  <div className="onu-modal-table-wrap">
                    <table className="onu-modal-table">
                      <thead>
                        <tr>
                          <th>Device</th>
                          <th>Port</th>
                          <th>VLAN</th>
                          <th>Last Seen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seenOn.map((m, i) => (
                          <tr key={i}>
                            <td>{m.device_name || m.device_ip || "--"}</td>
                            <td>{m.port || "--"}</td>
                            <td>{m.vlan ?? "--"}</td>
                            <td className="onu-mono">{fmtDate(m.last_seen)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnuDetailsModal;